import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Modal from './Modal';
import * as dateUtils from '../utils/dateUtils';

function UserDetail({ userInfo }) {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [timeRecords, setTimeRecords] = useState([]);
  const [formattedRecords, setFormattedRecords] = useState({});
  const [shifts, setShifts] = useState([]);
  const [formattedShifts, setFormattedShifts] = useState({});
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('attendance');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEditRecordModalOpen, setIsEditRecordModalOpen] = useState(false);
  const [isEditShiftModalOpen, setIsEditShiftModalOpen] = useState(false);
  const [isEditGroupAttributesModalOpen, setIsEditGroupAttributesModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedShift, setSelectedShift] = useState(null);
  const [editData, setEditData] = useState({
    name: '',
    role: '',
    clock_in: '',
    clock_out: '',
    note: '',
    modification_reason: '',
    break_time: '1'
  });
  const [editShiftData, setEditShiftData] = useState({
    start_time: '',
    end_time: '',
    is_workday: true
  });
  const [message, setMessage] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [workTypeFilter, setWorkTypeFilter] = useState('all'); // 業務タイプフィルター
  const [internalWorkRecords, setInternalWorkRecords] = useState([]); // 社内業務記録
  
  // グループ属性関連の状態
  const [groupAttributes, setGroupAttributes] = useState({
    client_company_id: '',
    job_assignment_id: '',
    employment_type_id: ''
  });
  const [clientCompanies, setClientCompanies] = useState([]);
  const [jobAssignments, setJobAssignments] = useState([]);
  const [employmentTypes, setEmploymentTypes] = useState([]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // 管理者権限チェック
    if (!userInfo || (userInfo.role !== 'admin' && userInfo.role !== 'super_admin')) {
      navigate('/time-clock');
      return;
    }

    fetchUserDetails();
    fetchTimeRecords();
    fetchShifts();
    
    // スーパー管理者の場合、グループ属性関連のデータを取得
    if (userInfo.role === 'super_admin') {
      fetchUserGroupAttributes();
      fetchClientCompanies();
      fetchEmploymentTypes();
    }
  }, [userId, selectedMonth, userInfo, navigate]);
  
  // クライアント企業が変更されたときに担当業務を取得
  useEffect(() => {
    if (groupAttributes.client_company_id) {
      fetchJobAssignments(groupAttributes.client_company_id);
    }
  }, [groupAttributes.client_company_id]);

  // ユーザー詳細情報を取得
  const fetchUserDetails = async () => {
    try {
      const response = await fetch(`/api/admin/users/${userId}`, {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('ユーザー情報の取得に失敗しました');
      }

      const data = await response.json();
      setUser(data);
      setEditData({
        name: data.name,
        role: data.role,
        clock_in: '',
        clock_out: '',
        note: '',
        modification_reason: '',
        break_time: '1'
      });
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  // 勤怠記録を取得
  const fetchTimeRecords = async () => {
    try {
      const response = await fetch(`/api/admin/users/${userId}/time-records?month=${selectedMonth}`, {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('勤怠記録の取得に失敗しました');
      }

      const data = await response.json();
      console.log('取得した勤怠記録:', data); // デバッグ用
      setTimeRecords(data);
      
      // 日付ごとにデータを整形
      const formattedData = {};
      
      data.forEach(record => {
        if (!record || !record.recorded_at) return;
        
        // 日付文字列を生成（データベースの日付は既に日本時間）
        const dateStr = dateUtils.formatDBDateToJST(record.recorded_at);
        console.log(`レコードID: ${record.id}, 元の日付: ${record.recorded_at}, 変換後: ${dateStr}`);
        
        if (!formattedData[dateStr]) {
          formattedData[dateStr] = {
            date: dateStr,
            records: []
          };
        }
        
        formattedData[dateStr].records.push(record);
      });
      
      // 各日付の出勤・退勤時間を特定
      Object.keys(formattedData).forEach(dateStr => {
        const dayRecords = formattedData[dateStr].records;
        let clockIn = null;
        let clockOut = null;
        let note = '';
        let break_time = null;
        let hasNormalRecords = false; // 通常の打刻記録があるかどうかのフラグ
        let absenceRecordId = null; // 欠勤記録のIDを保存
        
        console.log(`日付 ${dateStr} の処理開始 - レコード数: ${dayRecords.length}`);
        
        // まず通常の打刻記録があるか確認
        dayRecords.forEach(record => {
          if (record.record_type === 'clock_in' || record.record_type === 'in' ||
              record.record_type === 'clock_out' || record.record_type === 'out') {
            hasNormalRecords = true;
          }
        });
        
        console.log(`日付 ${dateStr} - 通常の打刻記録あり: ${hasNormalRecords}`);
        
        dayRecords.forEach(record => {
          console.log(`日付 ${dateStr} - レコードID: ${record.id}, タイプ: ${record.record_type}, 記録時間: ${record.recorded_at}`);
          
          // 欠勤記録の場合（通常の打刻記録がない場合のみ処理）
          if (record.record_type === 'absence' && !hasNormalRecords) {
            // 欠勤記録の備考から「欠勤: 」のプレフィックスを削除
            let cleanedNote = record.note || '';
            if (cleanedNote.startsWith('欠勤: ')) {
              cleanedNote = cleanedNote.substring(4);
            }
            // 備考欄が空の場合は「欠勤」と表示
            note = cleanedNote || '欠勤';
            absenceRecordId = record.id;
            break_time = null; // 欠勤の場合は休憩時間をnullに設定
            console.log(`日付 ${dateStr} - 欠勤記録を処理: ${note}, ID: ${absenceRecordId}`);
            return;
          }
          
          // record_typeが 'clock_in' または 'in' の場合
          if (record.record_type === 'clock_in' || record.record_type === 'in') {
            // 日時をJST変換して使用
            const timeStr = dateUtils.formatTimeToJST(record.recorded_at);
            console.log('出勤記録ID:', record.id);
            console.log('出勤記録の元のUTC時間:', record.recorded_at);
            console.log('出勤記録の日本時間変換結果:', timeStr);
            
            if (!clockIn || timeStr < clockIn) {
              clockIn = timeStr;
              console.log(`日付 ${dateStr} - 出勤時間を更新: ${clockIn}`);
            }
          } 
          // record_typeが 'clock_out' または 'out' の場合
          else if (record.record_type === 'clock_out' || record.record_type === 'out') {
            // 日時をJST変換して使用
            const timeStr = dateUtils.formatTimeToJST(record.recorded_at);
            console.log('退勤記録ID:', record.id);
            console.log('退勤記録の元のUTC時間:', record.recorded_at);
            console.log('退勤記録の日本時間変換結果:', timeStr);
            
            if (!clockOut || timeStr > clockOut) {
              clockOut = timeStr;
              console.log(`日付 ${dateStr} - 退勤時間を更新: ${clockOut}`);
            }
          }
          
          if (record.note && (record.record_type === 'clock_in' || record.record_type === 'in' || 
                             record.record_type === 'clock_out' || record.record_type === 'out')) {
            note = record.note;
          }

          // 休憩時間の処理 - 最新の値を優先（修正された値を使用）
          const allRecords = dayRecords.filter(r => r.record_type === 'clock_in' || r.record_type === 'in' || r.record_type === 'clock_out' || r.record_type === 'out');
          const latestModifiedRecord = allRecords
            .filter(record => record.break_time !== null && record.break_time !== undefined)
            .sort((a, b) => {
              const aTime = new Date(a.last_modified_at || a.recorded_at).getTime();
              const bTime = new Date(b.last_modified_at || b.recorded_at).getTime();
              return bTime - aTime; // 新しい順
            })[0];
          
          if (latestModifiedRecord) {
            // 数値を「時間:分」形式に変換
            break_time = `${Math.floor(latestModifiedRecord.break_time / 60)}:${latestModifiedRecord.break_time % 60 < 10 ? '0' + (latestModifiedRecord.break_time % 60) : latestModifiedRecord.break_time % 60}`;
          } else {
            break_time = null;
          }
        });
        
        formattedData[dateStr].clock_in = clockIn;
        formattedData[dateStr].clock_out = clockOut;
        formattedData[dateStr].note = note;
        formattedData[dateStr].break_time = break_time;
        formattedData[dateStr].id = dayRecords[0]?.id; // 最初のレコードIDを使用
        formattedData[dateStr].absenceRecordId = absenceRecordId; // 欠勤記録のIDを保存
      });
      
      console.log('整形した勤怠記録:', formattedData); // デバッグ用
      setFormattedRecords(formattedData);
    } catch (err) {
      console.error('Error fetching time records:', err);
      setMessage('勤怠記録の取得に失敗しました: ' + err.message);
    }
  };

  // シフト情報を取得
  const fetchShifts = async () => {
    try {
      const response = await fetch(`/api/admin/users/${userId}/shifts?month=${selectedMonth}`, {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('シフト情報の取得に失敗しました');
      }

      const data = await response.json();
      setShifts(data);
      
      // 日付ごとにデータを整形
      const formattedData = {};
      
      data.forEach(shift => {
        // 統一されたDB日付変換関数を使用
        const dateStr = dateUtils.formatDBDateToJST(shift.date);
        console.log('Original shift date:', shift.date);
        console.log('Converted shift date to JST:', dateStr);
        
        formattedData[dateStr] = {
          startTime: shift.start_time,
          endTime: shift.end_time,
          is_workday: shift.is_workday
        };
      });
      
      setFormattedShifts(formattedData);
    } catch (err) {
      console.error('Error fetching shifts:', err);
      setMessage('シフト情報の取得に失敗しました: ' + err.message);
    }
  };

  // 社内業務記録を取得
  const fetchInternalWorkRecords = async () => {
    try {
      const response = await fetch(`/api/internal-work/users/${userId}?month=${selectedMonth}`, {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('社内業務記録の取得に失敗しました');
      }

      const data = await response.json();
      
      // 日付の処理を修正
      const formattedData = data.map(record => {
        // 日付が正しいフォーマットであることを確認
        if (record.date) {
          // 日付のフォーマットを確認
          console.log('社内業務記録の日付:', record.date);
          
          // 日付が既にJST形式であることを確認
          const dateObj = new Date(record.date);
          console.log('変換後の日付オブジェクト:', dateObj);
          
          // 日付を正規化（YYYY-MM-DD形式に）
          const normalizedDate = dateUtils.formatDBDateToJST(record.date);
          console.log('正規化された日付:', normalizedDate);
          
          return {
            ...record,
            // 正規化された日付を使用
            date: normalizedDate
          };
        }
        return record;
      });
      
      setInternalWorkRecords(formattedData);
    } catch (error) {
      console.error('Error fetching internal work records:', error);
      setMessage('社内業務記録の取得に失敗しました: ' + error.message);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([
          fetchUserDetails(),
          fetchTimeRecords(),
          fetchShifts(),
          fetchUserGroupAttributes(),
          fetchInternalWorkRecords() // 社内業務記録も取得
        ]);
      } catch (error) {
        console.error('データ取得エラー:', error);
        setMessage('データの取得に失敗しました');
      }
    };

    fetchData();
  }, [userId, selectedMonth]);

  // ユーザーのグループ属性を取得
  const fetchUserGroupAttributes = async () => {
    try {
      const response = await fetch(`/api/admin/users/${userId}/group-attributes`, {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.error('グループ属性の取得に失敗しました');
        return;
      }

      const data = await response.json();
      setGroupAttributes(data);
      
      // クライアント企業IDがあれば、その担当業務を取得
      if (data.client_company_id) {
        fetchJobAssignments(data.client_company_id);
      }
    } catch (error) {
      console.error('Error fetching group attributes:', error);
    }
  };
  
  // クライアント企業の取得
  const fetchClientCompanies = async () => {
    try {
      const response = await fetch('/api/admin/client-companies', {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('クライアント企業の取得に失敗しました');
      }

      const data = await response.json();
      setClientCompanies(data);
    } catch (error) {
      console.error('Error fetching client companies:', error);
    }
  };
  
  // 担当業務の取得
  const fetchJobAssignments = async (clientCompanyId) => {
    try {
      const response = await fetch(`/api/admin/client-companies/${clientCompanyId}/job-assignments`, {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('担当業務の取得に失敗しました');
      }

      const data = await response.json();
      setJobAssignments(data);
    } catch (error) {
      console.error('Error fetching job assignments:', error);
    }
  };
  
  // 雇用区分の取得
  const fetchEmploymentTypes = async () => {
    try {
      const response = await fetch('/api/admin/employment-types', {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('雇用区分の取得に失敗しました');
      }

      const data = await response.json();
      setEmploymentTypes(data);
    } catch (error) {
      console.error('Error fetching employment types:', error);
    }
  };
  
  // グループ属性の編集モーダルを開く
  const handleEditGroupAttributes = () => {
    if (userInfo.role !== 'super_admin') {
      alert('この操作を行う権限がありません');
      return;
    }
    setIsEditGroupAttributesModalOpen(true);
  };
  
  // グループ属性の変更を処理
  const handleGroupAttributeChange = (e) => {
    const { name, value } = e.target;
    setGroupAttributes(prev => ({
      ...prev,
      [name]: value
    }));
    
    // クライアント企業が変更された場合、担当業務をリセット
    if (name === 'client_company_id') {
      setGroupAttributes(prev => ({
        ...prev,
        job_assignment_id: ''
      }));
    }
  };
  
  // グループ属性の保存
  const handleSaveGroupAttributes = async () => {
    try {
      const response = await fetch(`/api/admin/users/${userId}/group-attributes`, {
        method: 'PUT',
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(groupAttributes)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'グループ属性の更新に失敗しました');
      }

      setIsEditGroupAttributesModalOpen(false);
      setMessage('グループ属性を更新しました');
    } catch (error) {
      console.error('Error saving group attributes:', error);
      setError(error.message);
    }
  };

  const handleMonthChange = (e) => {
    setSelectedMonth(e.target.value);
  };

  const handleEditUser = () => {
    if (userInfo.role !== 'super_admin') {
      alert('この操作を行う権限がありません');
      return;
    }
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      const response = await fetch(`/api/admin/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editData)
      });

      if (!response.ok) {
        throw new Error('ユーザー情報の更新に失敗しました');
      }

      const updatedUser = await response.json();
      setUser(updatedUser);
      setIsEditModalOpen(false);
      alert('ユーザー情報を更新しました');
    } catch (err) {
      alert(err.message);
    }
  };

  // 勤怠記録の編集ボタンハンドラー
  const handleEditRecord = (dateStr) => {
    console.log('管理者ページ: 修正ボタンがクリックされました:', dateStr);
    const record = formattedRecords[dateStr] || { date: dateStr };
    
    console.log('修正対象の記録:', record);

    // 日付から対象の記録を特定
    const recordsForDate = timeRecords.filter(r => {
      if (!r || !r.recorded_at) return false;
      // データベースの日付は既に日本時間なので、formatDBDateToJSTを使用
      const recordDateStr = dateUtils.formatDBDateToJST(r.recorded_at);
      console.log(`日付比較: レコードID ${r.id}, 日付 ${r.recorded_at} -> ${recordDateStr}, 比較対象 ${dateStr}, 一致: ${recordDateStr === dateStr}`);
      return recordDateStr === dateStr;
    });
    
    console.log('対象日の記録:', recordsForDate);
    
    // レコードが存在しない場合は新規作成モードとして扱う
    const isNewRecord = recordsForDate.length === 0;
    console.log('新規作成モード:', isNewRecord);
    
    // 最初のレコードのIDを使用、または新規作成の場合は 'new'
    const recordId = isNewRecord ? 'new' : recordsForDate[0].id;
    console.log('選択されたレコードID:', recordId);

    setSelectedRecord({
      id: recordId,
      date: dateStr,
      ...record
    });

    // 表示されている時間に最も近い出勤・退勤レコードを特定
    const inRecords = recordsForDate.filter(r => r.record_type === 'in' || r.record_type === 'clock_in');
    const outRecords = recordsForDate.filter(r => r.record_type === 'out' || r.record_type === 'clock_out');
    
    // 表示されている出勤時間に対応するレコードを特定
    let inRecord = null;
    if (inRecords.length > 0 && record.clock_in) {
      // 表示されている時間と一致するレコードを探す
      const displayTime = record.clock_in;
      inRecord = inRecords.find(r => {
        const timeStr = dateUtils.formatTimeToJST(r.recorded_at);
        return timeStr === displayTime;
      }) || inRecords[0]; // 見つからなければ最初のレコードを使用
    }
    
    // 表示されている退勤時間に対応するレコードを特定
    let outRecord = null;
    if (outRecords.length > 0 && record.clock_out) {
      // 表示されている時間と一致するレコードを探す
      const displayTime = record.clock_out;
      outRecord = outRecords.find(r => {
        const timeStr = dateUtils.formatTimeToJST(r.recorded_at);
        return timeStr === displayTime;
      }) || outRecords[0]; // 見つからなければ最初のレコードを使用
    }
    
    const inRecordId = inRecord ? inRecord.id : null;
    const outRecordId = outRecord ? outRecord.id : null;
    
    // 休憩時間と備考のレコードIDを特定
    // 休憩時間は出勤または退勤レコードに紐づいている可能性がある
    const recordWithBreakTime = recordsForDate.find(r => 
      r.break_time && (r.record_type === 'in' || r.record_type === 'clock_in' || 
                      r.record_type === 'out' || r.record_type === 'clock_out')
    );
    const breakTimeRecordId = recordWithBreakTime ? recordWithBreakTime.id : 
                             (outRecord ? outRecord.id : (inRecord ? inRecord.id : null));
    
    // 欠勤記録を特定
    const absenceRecord = recordsForDate.find(r => r.record_type === 'absence');
    const absenceRecordId = absenceRecord ? absenceRecord.id : null;
    
    // 備考のレコードIDを特定（出勤・退勤または欠勤レコードのIDを使用）
    const recordWithNote = recordsForDate.find(r => r.note);
    const noteRecordId = recordWithNote ? recordWithNote.id :
                        (outRecord ? outRecord.id : (inRecord ? inRecord.id : (absenceRecord ? absenceRecord.id : null)));

    setEditData({
      name: record.name || '',
      role: record.role || '',
      clock_in: record.clock_in || '',
      clock_out: record.clock_out || '',
      note: record.note || '',
      modification_reason: '管理者による修正',
      break_time: '1'
    });
    
    console.log('編集モーダルを開きます');
    setIsEditRecordModalOpen(true);
  };

  // シフトの編集ボタンハンドラー
  const handleEditShift = (dateStr) => {
    const shift = formattedShifts[dateStr] || { startTime: '', endTime: '', is_workday: true };
    
    setSelectedShift({
      date: dateStr,
      ...shift
    });

    setEditShiftData({
      start_time: shift.startTime || '',
      end_time: shift.endTime || '',
      is_workday: shift.is_workday !== false
    });
    
    setIsEditShiftModalOpen(true);
  };

  // 勤怠記録の編集を保存
  const handleSaveRecordEdit = async () => {
    try {
      if (!selectedRecord) {
        setMessage('記録が選択されていません');
        return;
      }

      // 管理者権限チェック
      if (!userInfo || (userInfo.role !== 'admin' && userInfo.role !== 'super_admin')) {
        setMessage('この操作を行う権限がありません');
        console.log('権限エラー: 管理者権限が必要です', userInfo);
        return;
      }

      // 管理者による修正では自動的に理由を設定

      // 出勤時間と退勤時間の検証
      let clockIn = editData.clock_in;
      let clockOut = editData.clock_out;

      // 時間形式の検証
      const timeRegex = /^([01]?[0-9]|2[0-3]):([0-5][0-9])(?::([0-5][0-9]))?$/;
      
      if (clockIn && !timeRegex.test(clockIn)) {
        setMessage('無効な出勤時間形式です。HH:MM または HH:MM:SS 形式で入力してください');
        return;
      }
      
      if (clockOut && !timeRegex.test(clockOut)) {
        setMessage('無効な退勤時間形式です。HH:MM または HH:MM:SS 形式で入力してください');
        return;
      }

      // 日本時間の時間文字列を取得
      console.log('使用するレコードID:', selectedRecord.id);
      
      // 日本時間の日付文字列
      const dateStr = selectedRecord.date;
      console.log('出勤時間（日本時間）:', clockIn);
      console.log('退勤時間（日本時間）:', clockOut);
      
      // フォーマット後の時間
      const formattedTimes = {
        clockIn: clockIn,
        clockOut: clockOut
      };
      console.log('フォーマット後の時間:', formattedTimes);

      // APIリクエスト - 管理者用エンドポイントを使用
      const url = `/api/admin/users/${userId}/time-records/${selectedRecord.id}`;
      console.log('リクエスト先URL:', url);
      
      const requestBody = {
        date: dateStr,
        clock_in: formattedTimes.clockIn,
        clock_out: formattedTimes.clockOut,
        note: editData.note,
        modification_reason: editData.modification_reason,
        break_time: editData.break_time
      };
      console.log('リクエストボディ:', requestBody);
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      
      console.log('レスポンスステータス:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.log('エラーレスポンス:', errorData);
        throw new Error(errorData.message || '勤怠記録の更新に失敗しました');
      }
      
      const result = await response.json();
      console.log('修正結果:', result);
      
      setMessage('勤怠記録を更新しました');
      setIsEditRecordModalOpen(false);
      fetchTimeRecords(); // 勤怠記録を再取得
    } catch (err) {
      console.log('修正保存エラー:', err);
      setMessage('勤怠記録の更新に失敗しました: ' + err.message);
    }
  };

  // シフト情報の更新を保存
  const handleSaveShiftEdit = async () => {
    if (!selectedShift) {
      setMessage('修正対象のシフトが見つかりません');
      return;
    }

    // 管理者権限チェック
    if (!userInfo || (userInfo.role !== 'admin' && userInfo.role !== 'super_admin')) {
      setMessage('この操作を行う権限がありません');
      console.log('権限エラー: 管理者権限が必要です', userInfo);
      return;
    }

    try {
      console.log('送信するシフトデータ:', {
        date: selectedShift.date,
        start_time: editShiftData.start_time,
        end_time: editShiftData.end_time,
        is_workday: editShiftData.is_workday
      }); // デバッグ用
      
      const response = await fetch(`/api/admin/users/${userId}/shifts/${selectedShift.id || 'new'}`, {
        method: selectedShift.id ? 'PUT' : 'POST',
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          date: selectedShift.date,
          start_time: editShiftData.start_time,
          end_time: editShiftData.end_time,
          is_workday: editShiftData.is_workday
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'シフト情報の更新に失敗しました');
      }

      setIsEditShiftModalOpen(false);
      setMessage('シフト情報を更新しました');
      fetchShifts(); // データを再取得
    } catch (err) {
      setMessage(err.message);
    }
  };

  // 特定のフィールドを削除するハンドラー
  const handleDeleteField = async (recordId, field) => {
    try {
      if (!recordId) {
        setMessage('削除対象の記録が見つかりません');
        return;
      }

      // 管理者権限チェック
      if (!userInfo || (userInfo.role !== 'admin' && userInfo.role !== 'super_admin')) {
        setMessage('この操作を行う権限がありません');
        console.log('権限エラー: 管理者権限が必要です', userInfo);
        return;
      }

      console.log(`${field}を削除します:`, recordId);

      // APIリクエスト - 管理者用エンドポイントを使用
      const url = `/api/admin/users/${userId}/time-records/${recordId}/field`;
      console.log('削除リクエスト先URL:', url);
      
      // 削除前に現在のデータを保存
      const currentRecords = {...formattedRecords};
      
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ field })
      });
      
      console.log('削除レスポンスステータス:', response.status);
      
      if (!response.ok) {
        // レスポンスのContent-Typeをチェック
        const contentType = response.headers.get('content-type');
        let errorMessage = '';
        
        if (contentType && contentType.includes('application/json')) {
          // JSONレスポンスの場合
          const errorData = await response.json();
          console.log('削除エラーレスポンス (JSON):', errorData);
          errorMessage = errorData.message || `${field}の削除に失敗しました`;
        } else {
          // テキストまたはその他のレスポンスの場合
          const errorText = await response.text();
          console.log('削除エラーレスポンス (テキスト):', errorText);
          errorMessage = `${field}の削除に失敗しました: サーバーエラー`;
        }
        
        throw new Error(errorMessage);
      }
      
      const result = await response.json();
      console.log('削除結果:', result);
      
      setMessage(`${field === 'clock_in' ? '出勤時間' : field === 'clock_out' ? '退勤時間' : field === 'break_time' ? '休憩時間' : field === 'absence' ? '欠勤記録' : '備考'}を削除しました`);
      
      // データを再取得する前に一時的に現在のデータを表示
      setFormattedRecords(currentRecords);
      
      // 少し遅延させてからデータを再取得
      setTimeout(() => {
        fetchTimeRecords(); // 勤怠記録を再取得
      }, 500);
    } catch (err) {
      console.log('削除エラー:', err);
      setMessage(`${field}の削除に失敗しました: ` + err.message);
    }
  };

  // 日時のフォーマット
  const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return '';
    return dateUtils.formatDateToJST(dateTimeStr) + ' ' + dateUtils.formatTimeToJST(dateTimeStr);
  };

  // 日付のフォーマット
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return dateUtils.formatDateJP(dateStr);
  };

  // 月の日数を取得
  // 月の日数を取得する関数
  const getDaysInMonth = (yearMonth) => {
    const [year, month] = yearMonth.split('-').map(Number);
    return new Date(year, month, 0).getDate();
  };

  // 日付の配列を生成する関数
  const generateDatesForMonth = (yearMonth) => {
    const [year, month] = yearMonth.split('-').map(Number);
    const daysInMonth = new Date(year, month, 0).getDate();
    const dates = [];

    for (let day = 1; day <= daysInMonth; day++) {
      const paddedDay = day.toString().padStart(2, '0');
      const dateStr = `${yearMonth}-${paddedDay}`;
      dates.push(dateStr);
    }

    return dates;
  };

  // 週末かどうかを判定
  const isWeekend = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6; // 0: 日曜日, 6: 土曜日
  };

  // 時間文字列を分に変換するヘルパー関数
  const timeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // 分を時間文字列に変換するヘルパー関数
  const minutesToTime = (minutes) => {
    if (minutes <= 0) return null;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}:${mins < 10 ? '0' + mins : mins}`;
  };

  // フィルタリングされたレコードを取得
  const getFilteredRecords = () => {
    const dates = generateDatesForMonth(selectedMonth);
    
    // フィルタリング
    const filteredDates = dates.filter(dateStr => {
      if (workTypeFilter === 'internal') {
        // 社内業務のみ表示
        const internalWork = internalWorkRecords.find(r => {
          if (!r.date) return false;
          const recordDate = dateUtils.formatDBDateToJST(r.date);
          return recordDate === dateStr;
        });
        return internalWork;
      } else if (workTypeFilter === 'client') {
        // クライアント業務のみ表示
        // 社内業務がある日も表示するように修正（日付自体は表示する）
        return true;
      }
      
      // 'all'の場合はすべての日付を表示
      return true;
    });
    
    // フィルタリング後のレコードを調整
    const adjustedRecords = { ...formattedRecords };
    
    if (workTypeFilter === 'internal') {
      // 社内業務のみの場合、社内業務の時間を出勤・退勤として表示
      filteredDates.forEach(dateStr => {
        const internalWork = internalWorkRecords.find(r => {
          if (!r.date) return false;
          const recordDate = dateUtils.formatDBDateToJST(r.date);
          return recordDate === dateStr;
        });
        if (internalWork && adjustedRecords[dateStr]) {
          adjustedRecords[dateStr] = {
            ...adjustedRecords[dateStr],
            clock_in: internalWork.start_time.substring(0, 5),
            clock_out: internalWork.end_time.substring(0, 5),
            note: internalWork.note || adjustedRecords[dateStr].note,
            is_internal_work: true
          };
        }
      });
    } else if (workTypeFilter === 'client') {
      // クライアント業務のみの場合、社内業務の時間を差し引く
      filteredDates.forEach(dateStr => {
        const record = adjustedRecords[dateStr];
        const internalWork = internalWorkRecords.find(r => {
          if (!r.date) return false;
          const recordDate = dateUtils.formatDBDateToJST(r.date);
          return recordDate === dateStr;
        });
        
        // 社内業務がある場合のみ調整
        if (record && record.clock_in && record.clock_out && internalWork) {
          // 出勤・退勤時間を分に変換
          const clockInMinutes = timeToMinutes(record.clock_in);
          const clockOutMinutes = timeToMinutes(record.clock_out);
          
          // 社内業務の時間を分に変換
          const internalStartMinutes = timeToMinutes(internalWork.start_time.substring(0, 5));
          const internalEndMinutes = timeToMinutes(internalWork.end_time.substring(0, 5));
          
          // 社内業務の時間を差し引く
          let adjustedClockIn = clockInMinutes;
          let adjustedClockOut = clockOutMinutes;
          
          // 社内業務が出勤時間より後に始まる場合
          if (internalStartMinutes > clockInMinutes) {
            adjustedClockIn = clockInMinutes;
            
            // 社内業務が退勤時間より前に終わる場合
            if (internalEndMinutes < clockOutMinutes) {
              // 社内業務の時間を差し引く（出勤時間は変わらず、退勤時間から社内業務時間を引く）
              const internalDuration = internalEndMinutes - internalStartMinutes;
              adjustedClockOut = clockOutMinutes - internalDuration;
            } else {
              // 社内業務が退勤時間まで続く場合、出勤時間から社内業務開始までを表示
              adjustedClockOut = internalStartMinutes;
            }
          } else {
            // 社内業務が出勤時間から始まる場合
            if (internalEndMinutes < clockOutMinutes) {
              // 社内業務終了後から退勤までを表示
              adjustedClockIn = internalEndMinutes;
              adjustedClockOut = clockOutMinutes;
            } else {
              // 社内業務が全時間を占める場合、クライアント業務なし
              adjustedClockIn = null;
              adjustedClockOut = null;
            }
          }
          
          // 調整した時間を設定
          adjustedRecords[dateStr] = {
            ...record,
            clock_in: adjustedClockIn ? minutesToTime(adjustedClockIn) : null,
            clock_out: adjustedClockOut ? minutesToTime(adjustedClockOut) : null,
            is_client_work: true
          };
        }
      });
    } else if (workTypeFilter === 'all') {
      // すべて表示の場合、社内業務がある日は実際の時間フィールドに社内業務の時間を表示
      filteredDates.forEach(dateStr => {
        const record = adjustedRecords[dateStr];
        const internalWork = internalWorkRecords.find(r => {
          if (!r.date) return false;
          const recordDate = dateUtils.formatDBDateToJST(r.date);
          return recordDate === dateStr;
        });
        
        // 社内業務がある場合、社内業務の時間を出勤・退勤・休憩時間として表示
        if (internalWork) {
          const internalStartTime = internalWork.start_time.substring(0, 5);
          const internalEndTime = internalWork.end_time.substring(0, 5);
          const internalBreakTime = internalWork.break_time ? `${Math.floor(internalWork.break_time / 60)}:${String(internalWork.break_time % 60).padStart(2, '0')}` : null;
          
          // 社内業務の時間を実際のフィールドに設定
          adjustedRecords[dateStr] = {
            ...record,
            clock_in: internalStartTime,
            clock_out: internalEndTime,
            break_time: internalBreakTime,
            note: internalWork.note || record?.note,
            is_internal_work: true,
            has_internal_work: true
          };
        }
      });
    }
    
    return { dates: filteredDates, formattedRecords: adjustedRecords };
  };

  if (loading) {
    return <div className="flex justify-center items-center h-screen">読み込み中...</div>;
  }

  if (error) {
    return <div className="text-red-500 p-4">{error}</div>;
  }

  return (
    <div className="container mx-auto p-4">
      <div className="mb-4">
        <button 
          onClick={() => navigate('/admin')}
          className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
        >
          ← 戻る
        </button>
      </div>

      <div className="bg-white shadow-md rounded-lg p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold">ユーザー詳細</h1>
          {userInfo && userInfo.role === 'super_admin' && (
            <div>
              <button 
                onClick={handleEditUser}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded mr-2"
              >
                基本情報編集
              </button>
              <button 
                onClick={handleEditGroupAttributes}
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
              >
                グループ属性編集
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-gray-600">社員ID:</p>
            <p className="font-semibold">{user.employee_id}</p>
          </div>
          <div>
            <p className="text-gray-600">名前:</p>
            <p className="font-semibold">{user.name}</p>
          </div>
          <div>
            <p className="text-gray-600">権限:</p>
            <p className="font-semibold">
              {user.role === 'super_admin' ? 'スーパー管理者' : 
               user.role === 'admin' ? '管理者' : 'ユーザー'}
            </p>
          </div>
          <div>
            <p className="text-gray-600">登録日:</p>
            <p className="font-semibold">{new Date(user.created_at).toLocaleDateString()}</p>
          </div>
          
          {userInfo && userInfo.role === 'super_admin' && (
            <>
              <div>
                <p className="text-gray-600">クライアント企業:</p>
                <p className="font-semibold">
                  {groupAttributes.client_company_id ? 
                    clientCompanies.find(c => c.id === parseInt(groupAttributes.client_company_id))?.name || '未設定' : 
                    '未設定'}
                </p>
              </div>
              <div>
                <p className="text-gray-600">担当業務:</p>
                <p className="font-semibold">
                  {groupAttributes.job_assignment_id ? 
                    jobAssignments.find(j => j.id === parseInt(groupAttributes.job_assignment_id))?.name || '未設定' : 
                    '未設定'}
                </p>
              </div>
              <div>
                <p className="text-gray-600">雇用区分:</p>
                <p className="font-semibold">
                  {groupAttributes.employment_type_id ? 
                    employmentTypes.find(t => t.id === parseInt(groupAttributes.employment_type_id))?.name || '未設定' : 
                    '未設定'}
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {message && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
          {message}
        </div>
      )}

      <div className="mb-4">
        <div className="flex justify-between items-center">
          <div className="flex space-x-4">
            <button 
              onClick={() => setActiveTab('attendance')}
              className={`px-4 py-2 rounded ${activeTab === 'attendance' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
            >
              勤怠記録
            </button>
            <button 
              onClick={() => setActiveTab('shifts')}
              className={`px-4 py-2 rounded ${activeTab === 'shifts' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
            >
              シフト
            </button>
            {/* 集計情報を表示 */}
            {activeTab === 'shifts' && (() => {
              // 月次シフト集計を計算
              const shiftDays = Object.keys(formattedShifts).filter(dateStr => {
                const shift = formattedShifts[dateStr];
                return shift && shift.is_workday !== false && shift.startTime && shift.endTime;
              }).length;
              
              const totalHours = Object.keys(formattedShifts).reduce((total, dateStr) => {
                const shift = formattedShifts[dateStr];
                if (shift && shift.is_workday !== false && shift.startTime && shift.endTime) {
                  const startTime = shift.startTime.substring(0, 5);
                  const endTime = shift.endTime.substring(0, 5);
                  const [startHour, startMin] = startTime.split(':').map(Number);
                  const [endHour, endMin] = endTime.split(':').map(Number);
                  const duration = (endHour * 60 + endMin) - (startHour * 60 + startMin) - 60; // 1時間休憩を差し引く
                  return total + Math.max(0, duration);
                }
                return total;
              }, 0);
              
              const hours = Math.floor(totalHours / 60);
              const minutes = totalHours % 60;
              
              return shiftDays > 0 ? (
                <span className="ml-4 text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded">
                  {shiftDays}日 {hours}時間{minutes > 0 ? `${minutes}分` : ''}
                </span>
              ) : null;
            })()}
            {/* 勤怠記録集計情報を表示 */}
            {activeTab === 'attendance' && (() => {
              // 月次勤怠記録集計を計算
              const workDays = Object.keys(formattedRecords).filter(dateStr => {
                const record = formattedRecords[dateStr];
                return record && record.clock_in; // 出勤記録があるもの
              }).length;
              
              let totalMinutes = 0;
              let partialDays = 0; // 出勤のみで退勤していない日数
              
              Object.keys(formattedRecords).forEach(dateStr => {
                const record = formattedRecords[dateStr];
                if (record && record.clock_in) {
                  if (record.clock_out) {
                    // 出勤・退勤両方ある場合
                    const clockInTime = record.clock_in;
                    const clockOutTime = record.clock_out;
                    const [inHour, inMin] = clockInTime.split(':').map(Number);
                    const [outHour, outMin] = clockOutTime.split(':').map(Number);
                    
                    let workMinutes = (outHour * 60 + outMin) - (inHour * 60 + inMin);
                    
                    // 休憩時間を差し引く
                    if (record.break_time) {
                      const [breakHour, breakMin] = record.break_time.split(':').map(Number);
                      workMinutes -= (breakHour * 60 + breakMin);
                    } else {
                      workMinutes -= 60; // デフォルト1時間休憩
                    }
                    
                    totalMinutes += Math.max(0, workMinutes);
                  } else {
                    // 出勤のみの場合
                    partialDays++;
                  }
                }
              });
              
              const hours = Math.floor(totalMinutes / 60);
              const minutes = totalMinutes % 60;
              
              return workDays > 0 ? (
                <span className="ml-4 text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded">
                  {workDays}日 {hours}時間{minutes > 0 ? `${minutes}分` : ''}
                  {partialDays > 0 && <span className="text-orange-600"> (未退勤: {partialDays}日)</span>}
                </span>
              ) : null;
            })()}
          </div>
          <div>
            <input 
              type="month" 
              value={selectedMonth}
              onChange={handleMonthChange}
              className="border rounded p-2"
            />
          </div>
        </div>
        
        {/* 業務タイプフィルター */}
        {activeTab === 'attendance' && (
          <div className="flex items-center mt-4">
            <span className="mr-2 text-gray-700">業務タイプ:</span>
            <div className="flex border rounded overflow-hidden">
              <button
                className={`px-3 py-2 ${workTypeFilter === 'all' ? 'bg-blue-500 text-white' : 'bg-white text-gray-700'}`}
                onClick={() => setWorkTypeFilter('all')}
              >
                すべて
              </button>
              <button
                className={`px-3 py-2 ${workTypeFilter === 'client' ? 'bg-blue-500 text-white' : 'bg-white text-gray-700'}`}
                onClick={() => setWorkTypeFilter('client')}
              >
                クライアント業務
              </button>
              <button
                className={`px-3 py-2 ${workTypeFilter === 'internal' ? 'bg-blue-500 text-white' : 'bg-white text-gray-700'}`}
                onClick={() => setWorkTypeFilter('internal')}
              >
                社内業務
              </button>
            </div>
          </div>
        )}
      </div>

      {activeTab === 'attendance' && (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          {/* デスクトップ表示 */}
          {!isMobile && (
            <table className="min-w-full bg-white border">
              <thead>
                <tr>
                  <th className="px-4 py-2">日付</th>
                  <th className="px-4 py-2">シフト</th>
                  <th className="px-4 py-2">出勤</th>
                  <th className="px-4 py-2">退勤</th>
                  <th className="px-4 py-2">休憩時間</th>
                  <th className="px-4 py-2">備考</th>
                  <th className="px-4 py-2">操作</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // フィルタリングされたデータを取得
                  const { dates, formattedRecords: filteredRecords } = getFilteredRecords();
                  
                  return dates.map((dateStr, index) => {
                    const date = dateUtils.createSafeDate(dateStr);
                    const dayOfWeek = dateUtils.getDayOfWeek(dateStr);
                    const shift = formattedShifts[dateStr] || { startTime: '', endTime: '' };
                    const record = filteredRecords[dateStr] || {};
                    
                    // 出勤・退勤レコードのIDを取得
                    const recordsForDate = timeRecords.filter(r => {
                      if (!r || !r.recorded_at) return false;
                      // データベースの日付は既に日本時間なので、formatDBDateToJSTを使用
                      const recordDateStr = dateUtils.formatDBDateToJST(r.recorded_at);
                      console.log(`日付比較: レコードID ${r.id}, 日付 ${r.recorded_at} -> ${recordDateStr}, 比較対象 ${dateStr}, 一致: ${recordDateStr === dateStr}`);
                      return recordDateStr === dateStr;
                    });
                    
                    // 表示されている時間に最も近い出勤・退勤レコードを特定
                    const inRecords = recordsForDate.filter(r => r.record_type === 'in' || r.record_type === 'clock_in');
                    const outRecords = recordsForDate.filter(r => r.record_type === 'out' || r.record_type === 'clock_out');
                    
                    // 表示されている出勤時間に対応するレコードを特定
                    let inRecord = null;
                    if (inRecords.length > 0 && record.clock_in) {
                      // 表示されている時間と一致するレコードを探す
                      const displayTime = record.clock_in;
                      inRecord = inRecords.find(r => {
                        const timeStr = dateUtils.formatTimeToJST(r.recorded_at);
                        return timeStr === displayTime;
                      }) || inRecords[0]; // 見つからなければ最初のレコードを使用
                    }
                    
                    // 表示されている退勤時間に対応するレコードを特定
                    let outRecord = null;
                    if (outRecords.length > 0 && record.clock_out) {
                      // 表示されている時間と一致するレコードを探す
                      const displayTime = record.clock_out;
                      outRecord = outRecords.find(r => {
                        const timeStr = dateUtils.formatTimeToJST(r.recorded_at);
                        return timeStr === displayTime;
                      }) || outRecords[0]; // 見つからなければ最初のレコードを使用
                    }
                    
                    const inRecordId = inRecord ? inRecord.id : null;
                    const outRecordId = outRecord ? outRecord.id : null;
                    
                    // 休憩時間と備考のレコードIDを特定
                    // 休憩時間は出勤または退勤レコードに紐づいている可能性がある
                    const recordWithBreakTime = recordsForDate.find(r => 
                      r.break_time && (r.record_type === 'in' || r.record_type === 'clock_in' || 
                                      r.record_type === 'out' || r.record_type === 'clock_out')
                    );
                    const breakTimeRecordId = recordWithBreakTime ? recordWithBreakTime.id : 
                                             (outRecord ? outRecord.id : (inRecord ? inRecord.id : null));
                    
                    // 欠勤記録を特定
                    const absenceRecord = recordsForDate.find(r => r.record_type === 'absence');
                    const absenceRecordId = absenceRecord ? absenceRecord.id : null;
                    
                    // 備考のレコードIDを特定（出勤・退勤または欠勤レコードのIDを使用）
                    const recordWithNote = recordsForDate.find(r => r.note);
                    const noteRecordId = recordWithNote ? recordWithNote.id :
                                        (outRecord ? outRecord.id : (inRecord ? inRecord.id : (absenceRecord ? absenceRecord.id : null)));

                    return (
                      <tr key={index} className={isWeekend(date) ? 'bg-gray-100' : ''}>
                        <td className="border px-4 py-2">
                          {`${date.getDate()}日(${dayOfWeek})`}
                        </td>
                        <td className="border px-4 py-2">
                          {shift.startTime && shift.endTime ? 
                            `${shift.startTime.substring(0, 5)} - ${shift.endTime.substring(0, 5)}` : 
                            '---'}
                        </td>
                        <td className="border px-4 py-2 relative">
                          {record.clock_in ? (
                            <div className="flex flex-col">
                              <div className="flex items-center">
                                <span>{record.clock_in}</span>
                                {inRecordId && (
                                  <button 
                                    onClick={() => handleDeleteField(inRecordId, 'clock_in')}
                                    className="ml-2 text-red-500 hover:text-red-700"
                                    title="出勤時間を削除"
                                  >
                                    ×
                                  </button>
                                )}
                              </div>
                              {/* 出勤時のGPS情報を表示 */}
                              {recordsForDate && (() => {
                                const inRecord = recordsForDate.find(r => 
                                  (r.record_type === 'in' || r.record_type === 'clock_in') &&
                                  r.latitude && r.longitude
                                );
                                if (inRecord && inRecord.address_data && inRecord.address_data.results && inRecord.address_data.results[0]) {
                                  const fullAddress = inRecord.address_data.results[0].formatted_address;
                                  const cleanAddress = fullAddress.replace(/^日本、〒\d{3}-\d{4}\s*/, '');
                                  const mapUrl = `https://maps.google.com/maps?q=${inRecord.latitude},${inRecord.longitude}`;
                                  return (
                                    <a 
                                      href={mapUrl} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-xs text-gray-500 hover:text-blue-600 mt-0.5 block truncate max-w-32"
                                      title={cleanAddress}
                                    >
                                      📍 {cleanAddress.length > 15 ? cleanAddress.substring(0, 15) + '...' : cleanAddress}
                                    </a>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                          ) : '---'}
                        </td>
                        <td className="border px-4 py-2 relative">
                          {record.clock_out ? (
                            <div className="flex flex-col">
                              <div className="flex items-center">
                                <span>{record.clock_out}</span>
                                {outRecordId && (
                                  <button 
                                    onClick={() => handleDeleteField(outRecordId, 'clock_out')}
                                    className="ml-2 text-red-500 hover:text-red-700"
                                    title="退勤時間を削除"
                                  >
                                    ×
                                  </button>
                                )}
                              </div>
                              {/* 退勤時のGPS情報を表示 */}
                              {recordsForDate && (() => {
                                const outRecord = recordsForDate.find(r => 
                                  (r.record_type === 'out' || r.record_type === 'clock_out') &&
                                  r.latitude && r.longitude
                                );
                                if (outRecord && outRecord.address_data && outRecord.address_data.results && outRecord.address_data.results[0]) {
                                  const fullAddress = outRecord.address_data.results[0].formatted_address;
                                  const cleanAddress = fullAddress.replace(/^日本、〒\d{3}-\d{4}\s*/, '');
                                  const mapUrl = `https://maps.google.com/maps?q=${outRecord.latitude},${outRecord.longitude}`;
                                  return (
                                    <a 
                                      href={mapUrl} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-xs text-gray-500 hover:text-blue-600 mt-0.5 block truncate max-w-32"
                                      title={cleanAddress}
                                    >
                                      📍 {cleanAddress.length > 15 ? cleanAddress.substring(0, 15) + '...' : cleanAddress}
                                    </a>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                          ) : '---'}
                        </td>
                        <td className="border px-4 py-2 relative">
                          {record.break_time ? (
                            <div className="flex items-center">
                              <span>{record.break_time}</span>
                              {breakTimeRecordId && (
                                <button 
                                  onClick={() => handleDeleteField(breakTimeRecordId, 'break_time')}
                                  className="ml-2 text-red-500 hover:text-red-700"
                                  title="休憩時間を削除"
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          ) : '---'}
                        </td>
                        <td className="border px-4 py-2 relative">
                          {record.note ? (
                            <div className="flex items-center">
                              <span>{record.note}</span>
                              {noteRecordId && (
                                <button 
                                  onClick={() => handleDeleteField(noteRecordId, 'note')}
                                  className="ml-2 text-red-500 hover:text-red-700"
                                  title="備考を削除"
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          ) : '---'}
                          {absenceRecordId && (
                            <div className="flex items-center mt-1">
                              <span className="text-xs text-gray-500">欠勤記録</span>
                              <button 
                                onClick={() => handleDeleteField(absenceRecordId, 'absence')}
                                className="ml-2 text-red-500 hover:text-red-700"
                                title="欠勤記録を削除"
                              >
                                ×
                              </button>
                            </div>
                          )}
                        </td>
                        <td className="border px-4 py-2">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditRecord(dateStr)}
                              className="bg-blue-500 text-white px-2 py-1 rounded text-sm"
                            >
                              修正
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          )}

          {/* モバイル表示 */}
          {isMobile && (
            <div className="space-y-4">
              {(() => {
                // フィルタリングされたデータを取得
                const { dates, formattedRecords: filteredRecords } = getFilteredRecords();
                
                return dates.map((dateStr, index) => {
                  const date = dateUtils.createSafeDate(dateStr);
                  const dayOfWeek = dateUtils.getDayOfWeek(dateStr);
                  // const shift = formattedShifts[dateStr] || { startTime: '', endTime: '' };
                  const record = filteredRecords[dateStr] || {};
                  
                  // 出勤・退勤レコードのIDを取得
                  const recordsForDate = timeRecords.filter(r => {
                    if (!r || !r.recorded_at) return false;
                    // データベースの日付は既に日本時間なので、formatDBDateToJSTを使用
                    const recordDateStr = dateUtils.formatDBDateToJST(r.recorded_at);
                    console.log(`日付比較: レコードID ${r.id}, 日付 ${r.recorded_at} -> ${recordDateStr}, 比較対象 ${dateStr}, 一致: ${recordDateStr === dateStr}`);
                    return recordDateStr === dateStr;
                  });
                  
                  // 表示されている時間に最も近い出勤・退勤レコードを特定
                  const inRecords = recordsForDate.filter(r => r.record_type === 'in' || r.record_type === 'clock_in');
                  const outRecords = recordsForDate.filter(r => r.record_type === 'out' || r.record_type === 'clock_out');
                  
                  // 表示されている出勤時間に対応するレコードを特定
                  let inRecord = null;
                  if (inRecords.length > 0 && record.clock_in) {
                    // 表示されている時間と一致するレコードを探す
                    const displayTime = record.clock_in;
                    inRecord = inRecords.find(r => {
                      const timeStr = dateUtils.formatTimeToJST(r.recorded_at);
                      return timeStr === displayTime;
                    }) || inRecords[0]; // 見つからなければ最初のレコードを使用
                  }
                  
                  // 表示されている退勤時間に対応するレコードを特定
                  let outRecord = null;
                  if (outRecords.length > 0 && record.clock_out) {
                    // 表示されている時間と一致するレコードを探す
                    const displayTime = record.clock_out;
                    outRecord = outRecords.find(r => {
                      const timeStr = dateUtils.formatTimeToJST(r.recorded_at);
                      return timeStr === displayTime;
                    }) || outRecords[0]; // 見つからなければ最初のレコードを使用
                  }
                  
                  const inRecordId = inRecord ? inRecord.id : null;
                  const outRecordId = outRecord ? outRecord.id : null;
                  
                  // 休憩時間と備考のレコードIDを特定
                  // 休憩時間は出勤または退勤レコードに紐づいている可能性がある
                  const recordWithBreakTime = recordsForDate.find(r => 
                    r.break_time && (r.record_type === 'in' || r.record_type === 'clock_in' || 
                                    r.record_type === 'out' || r.record_type === 'clock_out')
                  );
                  const breakTimeRecordId = recordWithBreakTime ? recordWithBreakTime.id : 
                                           (outRecord ? outRecord.id : (inRecord ? inRecord.id : null));
                  
                  // 欠勤記録を特定
                  const absenceRecord = recordsForDate.find(r => r.record_type === 'absence');
                  const absenceRecordId = absenceRecord ? absenceRecord.id : null;
                  
                  // 備考のレコードIDを特定（出勤・退勤または欠勤レコードのIDを使用）
                  const recordWithNote = recordsForDate.find(r => r.note);
                  const noteRecordId = recordWithNote ? recordWithNote.id :
                                      (outRecord ? outRecord.id : (inRecord ? inRecord.id : (absenceRecord ? absenceRecord.id : null)));

                  return (
                    <div 
                      key={index} 
                      className={`bg-white rounded-lg shadow-md p-4 ${isWeekend(date) ? 'bg-gray-50' : ''}`}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="font-bold">
                          {`${date.getDate()}日(${dayOfWeek})`}
                        </h3>
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleEditRecord(dateStr)}
                            className="bg-blue-500 text-white px-2 py-1 rounded text-xs"
                          >
                            修正
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="flex flex-col">
                          <div className="flex items-center">
                            <span className="text-gray-600">出勤: </span>
                            <span className="font-medium ml-1">
                              {record.clock_in || '---'}
                            </span>
                            {record.clock_in && inRecordId && userInfo && (userInfo.role === 'admin' || userInfo.role === 'super_admin') && (
                              <button 
                                onClick={() => handleDeleteField(inRecordId, 'clock_in')}
                                className="ml-1 text-red-500 hover:text-red-700 text-xs"
                                title="出勤時間を削除"
                              >
                                ×
                              </button>
                            )}
                          </div>
                          {/* 出勤時のGPS情報を表示（モバイル版） */}
                          {recordsForDate && (() => {
                            const inRecord = recordsForDate.find(r => 
                              (r.record_type === 'in' || r.record_type === 'clock_in') &&
                              r.latitude && r.longitude
                            );
                            if (inRecord && inRecord.address_data && inRecord.address_data.results && inRecord.address_data.results[0]) {
                              const fullAddress = inRecord.address_data.results[0].formatted_address;
                              const cleanAddress = fullAddress.replace(/^日本、〒\d{3}-\d{4}\s*/, '');
                              const mapUrl = `https://maps.google.com/maps?q=${inRecord.latitude},${inRecord.longitude}`;
                              return (
                                <a 
                                  href={mapUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-xs text-gray-500 hover:text-blue-600 mt-0.5 block truncate"
                                  title={cleanAddress}
                                >
                                  📍 {cleanAddress.length > 18 ? cleanAddress.substring(0, 18) + '...' : cleanAddress}
                                </a>
                              );
                            }
                            return null;
                          })()}
                        </div>
                        <div className="flex flex-col">
                          <div className="flex items-center">
                            <span className="text-gray-600">退勤: </span>
                            <span className="font-medium ml-1">
                              {record.clock_out || '---'}
                            </span>
                            {record.clock_out && outRecordId && userInfo && (userInfo.role === 'admin' || userInfo.role === 'super_admin') && (
                              <button 
                                onClick={() => handleDeleteField(outRecordId, 'clock_out')}
                                className="ml-1 text-red-500 hover:text-red-700 text-xs"
                                title="退勤時間を削除"
                              >
                                ×
                              </button>
                            )}
                          </div>
                          {/* 退勤時のGPS情報を表示（モバイル版） */}
                          {recordsForDate && (() => {
                            const outRecord = recordsForDate.find(r => 
                              (r.record_type === 'out' || r.record_type === 'clock_out') &&
                              r.latitude && r.longitude
                            );
                            if (outRecord && outRecord.address_data && outRecord.address_data.results && outRecord.address_data.results[0]) {
                              const fullAddress = outRecord.address_data.results[0].formatted_address;
                              const cleanAddress = fullAddress.replace(/^日本、〒\d{3}-\d{4}\s*/, '');
                              const mapUrl = `https://maps.google.com/maps?q=${outRecord.latitude},${outRecord.longitude}`;
                              return (
                                <a 
                                  href={mapUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-xs text-gray-500 hover:text-blue-600 mt-0.5 block truncate"
                                  title={cleanAddress}
                                >
                                  📍 {cleanAddress.length > 18 ? cleanAddress.substring(0, 18) + '...' : cleanAddress}
                                </a>
                              );
                            }
                            return null;
                          })()}
                        </div>
                        <div className="flex items-center">
                          <span className="text-gray-600">休憩: </span>
                          <span className="font-medium ml-1">
                            {record.break_time && !absenceRecordId ? record.break_time : '---'}
                          </span>
                          {record.break_time && !absenceRecordId && breakTimeRecordId && userInfo && (userInfo.role === 'admin' || userInfo.role === 'super_admin') && (
                            <button 
                              onClick={() => handleDeleteField(breakTimeRecordId, 'break_time')}
                              className="ml-1 text-red-500 hover:text-red-700 text-xs"
                              title="休憩時間を削除"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      </div>

                      {record.note && (
                        <div className="mt-2 text-sm flex items-center">
                          <span className="text-gray-600">備考: </span>
                          <span className="font-medium ml-1">
                            {record.note || '---'}
                          </span>
                          {noteRecordId && userInfo && (userInfo.role === 'admin' || userInfo.role === 'super_admin') && (
                            <button 
                              onClick={() => handleDeleteField(noteRecordId, 'note')}
                              className="ml-1 text-red-500 hover:text-red-700 text-xs"
                              title="備考を削除"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      )}

                      {/* 欠勤記録の場合、欠勤記録自体を削除するボタンを表示（モバイル表示） */}
                      {absenceRecordId && userInfo && (userInfo.role === 'admin' || userInfo.role === 'super_admin') && (
                        <div className="mt-2 text-sm flex items-center">
                          <span className="text-gray-600">欠勤記録: </span>
                          <button 
                            onClick={() => handleDeleteField(absenceRecordId, 'absence')}
                            className="ml-1 text-red-500 hover:text-red-700 text-xs"
                            title="欠勤記録を削除"
                          >
                            削除
                          </button>
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </div>
      )}

      {activeTab === 'shifts' && (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          {/* デスクトップ表示 */}
          {!isMobile && (
            <table className="min-w-full bg-white border">
              <thead>
                <tr>
                  <th className="px-4 py-2">日付</th>
                  <th className="px-4 py-2">開始時間</th>
                  <th className="px-4 py-2">終了時間</th>
                  <th className="px-4 py-2">休憩時間</th>
                  <th className="px-4 py-2">勤務日</th>
                  <th className="px-4 py-2">操作</th>
                </tr>
              </thead>
              <tbody>
                {generateDatesForMonth(selectedMonth).map((dateStr, index) => {
                  const date = dateUtils.createSafeDate(dateStr);
                  const dayOfWeek = dateUtils.getDayOfWeek(dateStr);
                  const shift = formattedShifts[dateStr];

                  return (
                    <tr key={index} className={isWeekend(date) ? 'bg-gray-100' : ''}>
                      <td className="border px-4 py-2">
                        {`${date.getDate()}日(${dayOfWeek})`}
                      </td>
                      <td className="border px-4 py-2">
                        {shift?.startTime && shift?.endTime ? 
                          `${shift.startTime.substring(0, 5)} - ${shift.endTime.substring(0, 5)}` : 
                          '---'}
                      </td>
                      <td className="border px-4 py-2">
                        {shift?.is_workday !== false ? '1:00' : '---'}
                      </td>
                      <td className="border px-4 py-2">
                        {shift?.is_workday !== false ? '○' : '×'}
                      </td>
                      <td className="border px-4 py-2">
                        {shift?.is_workday !== false ? '○' : '×'}
                      </td>
                      <td className="border px-4 py-2">
                        <button
                          onClick={() => handleEditShift(dateStr)}
                          className="bg-blue-500 text-white px-2 py-1 rounded text-sm"
                        >
                          修正
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* モバイル表示 */}
          {isMobile && (
            <div className="space-y-4 p-2">
              {generateDatesForMonth(selectedMonth).map((dateStr, index) => {
                const date = dateUtils.createSafeDate(dateStr);
                const dayOfWeek = dateUtils.getDayOfWeek(dateStr);
                const shift = formattedShifts[dateStr];
                const isWeekendDay = dateUtils.isWeekend(dateStr);

                return (
                  <div 
                    key={index} 
                    className={`bg-white rounded-lg shadow-md p-4 ${isWeekendDay ? 'bg-gray-50' : ''}`}
                  >
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="font-bold">
                        {`${date.getDate()}日(${dayOfWeek})`}
                      </h3>
                      <button
                        onClick={() => handleEditShift(dateStr)}
                        className="bg-blue-500 text-white px-2 py-1 rounded text-xs"
                      >
                        修正
                      </button>
                    </div>

                    {shift?.is_workday !== false && (
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-gray-600">開始: </span>
                          <span className="font-medium">
                            {shift?.startTime && shift?.endTime ? 
                              `${shift.startTime.substring(0, 5)} - ${shift.endTime.substring(0, 5)}` : 
                              '---'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">終了: </span>
                          <span className="font-medium">
                            {shift?.is_workday !== false ? '1:00' : '---'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">休憩: </span>
                          <span className="font-medium">1:00</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ユーザー編集モーダル */}
      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>
        <div className="p-4">
          <h2 className="text-lg font-bold mb-4">ユーザー情報編集</h2>
          <div className="space-y-4">
            <div>
              <label className="block mb-1">名前</label>
              <input
                type="text"
                value={editData.name}
                onChange={(e) => setEditData({...editData, name: e.target.value})}
                className="w-full border rounded px-2 py-1"
              />
            </div>
            <div>
              <label className="block mb-1">権限</label>
              <select
                value={editData.role}
                onChange={(e) => setEditData({...editData, role: e.target.value})}
                className="w-full border rounded px-2 py-1"
              >
                <option value="user">一般ユーザー</option>
                <option value="admin">管理者</option>
                <option value="super_admin">スーパー管理者</option>
              </select>
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleSaveEdit}
                className="bg-blue-500 text-white px-4 py-2 rounded"
              >
                保存
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* 勤怠記録編集モーダル */}
      <Modal isOpen={isEditRecordModalOpen} onClose={() => setIsEditRecordModalOpen(false)}>
        <div className="p-4">
          <h2 className="text-lg font-bold mb-4">勤怠記録修正</h2>
          <div className="space-y-4">
            <div>
              <label className="block mb-1">出勤時刻</label>
              <input
                type="time"
                value={editData.clock_in}
                onChange={(e) => setEditData({...editData, clock_in: e.target.value})}
                className="border rounded px-2 py-1"
              />
            </div>
            <div>
              <label className="block mb-1">退勤時刻</label>
              <input
                type="time"
                value={editData.clock_out}
                onChange={(e) => setEditData({...editData, clock_out: e.target.value})}
                className="border rounded px-2 py-1"
              />
            </div>
            <div>
              <label className="block mb-1">休憩時間（時間）</label>
              <select
                value={editData.break_time || "1"}
                onChange={(e) => setEditData({...editData, break_time: e.target.value})}
                className="border rounded px-2 py-1"
              >
                <option value="0.5">0:30</option>
                <option value="1">1:00</option>
                <option value="1.5">1:30</option>
                <option value="2">2:00</option>
              </select>
            </div>
            <div>
              <label className="block mb-1">備考</label>
              <textarea
                value={editData.note}
                onChange={(e) => setEditData({...editData, note: e.target.value})}
                className="w-full border rounded px-2 py-1"
              />
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleSaveRecordEdit}
                className="bg-blue-500 text-white px-4 py-2 rounded"
              >
                保存
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* シフト編集モーダル */}
      <Modal isOpen={isEditShiftModalOpen} onClose={() => setIsEditShiftModalOpen(false)}>
        <div className="p-4">
          <h2 className="text-lg font-bold mb-4">シフト修正</h2>
          <div className="space-y-4">
            <div>
              <label className="block mb-1">開始時間</label>
              <input
                type="time"
                value={editShiftData.start_time}
                onChange={(e) => setEditShiftData({...editShiftData, start_time: e.target.value})}
                className="border rounded px-2 py-1"
              />
            </div>
            <div>
              <label className="block mb-1">終了時間</label>
              <input
                type="time"
                value={editShiftData.end_time}
                onChange={(e) => setEditShiftData({...editShiftData, end_time: e.target.value})}
                className="border rounded px-2 py-1"
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_workday"
                checked={editShiftData.is_workday}
                onChange={(e) => setEditShiftData({...editShiftData, is_workday: e.target.checked})}
                className="mr-2"
              />
              <label htmlFor="is_workday">勤務日</label>
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleSaveShiftEdit}
                className="bg-blue-500 text-white px-4 py-2 rounded"
              >
                保存
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* グループ属性編集モーダル */}
      <Modal 
        isOpen={isEditGroupAttributesModalOpen} 
        onClose={() => setIsEditGroupAttributesModalOpen(false)}
      >
        <h2 className="text-xl font-bold mb-4">グループ属性編集</h2>
        
        <div className="mb-4">
          <label className="block text-gray-700 mb-2" htmlFor="client_company_id">
            クライアント企業
          </label>
          <select
            id="client_company_id"
            name="client_company_id"
            value={groupAttributes.client_company_id || ''}
            onChange={handleGroupAttributeChange}
            className="w-full p-2 border rounded"
          >
            <option value="">選択してください</option>
            {clientCompanies.map(company => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
        </div>
        
        <div className="mb-4">
          <label className="block text-gray-700 mb-2" htmlFor="job_assignment_id">
            担当業務
          </label>
          <select
            id="job_assignment_id"
            name="job_assignment_id"
            value={groupAttributes.job_assignment_id || ''}
            onChange={handleGroupAttributeChange}
            className="w-full p-2 border rounded"
            disabled={!groupAttributes.client_company_id}
          >
            <option value="">選択してください</option>
            {jobAssignments.map(job => (
              <option key={job.id} value={job.id}>
                {job.name}
              </option>
            ))}
          </select>
          {!groupAttributes.client_company_id && (
            <p className="text-sm text-gray-500 mt-1">
              先にクライアント企業を選択してください
            </p>
          )}
        </div>
        
        <div className="mb-4">
          <label className="block text-gray-700 mb-2" htmlFor="employment_type_id">
            雇用区分
          </label>
          <select
            id="employment_type_id"
            name="employment_type_id"
            value={groupAttributes.employment_type_id || ''}
            onChange={handleGroupAttributeChange}
            className="w-full p-2 border rounded"
          >
            <option value="">選択してください</option>
            {employmentTypes.map(type => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </div>
        
        <div className="flex justify-end">
          <button
            onClick={() => setIsEditGroupAttributesModalOpen(false)}
            className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded mr-2"
          >
            キャンセル
          </button>
          <button
            onClick={handleSaveGroupAttributes}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
          >
            保存
          </button>
        </div>
      </Modal>
    </div>
  );
}

export default UserDetail; 